/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 포커스 추적클래스를 정의한다.
 *
 * @file /src/rootin/selection/Focus.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 19.
 */
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Editor } from '@rootin/core/Editor';
import { Html } from '@utils/Html';

export class Focus {
    /**
     * 마지막으로 포커스된 블록
     */
    block: Block | null = null;

    /**
     * 마지막으로 포커스된 엘리먼트
     */
    element: Element | null = null;

    /**
     * 마지막 마우스다운 지점.
     * resolveContainer() 가 호출되며 소모(consume) 후 버려진다.
     */
    #point: { x: number; y: number } | null = null;

    /**
     * 억류(suspend)된 포커스 상태 — 모달 등으로 포커스가 에디터 밖에 나가있는 동안, 복구에
     * 필요한 블록/엘리먼트/캐럿 상태를 기억해둔다.
     */
    #suspended: { block: Block | null; element: Element | null; caret: any } | null = null;

    /**
     * 에디터의 포커스 추적클래스를 정의한다.
     *
     * @param editor - 포커스를 추적할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 포커스가 에디터 밖(모달창 등)으로 나가기 전에, 지금 포커스/캐럿 상태를 기억해둔다.
     */
    suspend(): void {
        if (this.#suspended !== null) {
            return;
        }

        this.#suspended = {
            block: this.block,
            element: this.element,
            caret: this.element?.captureCaret() ?? null,
        };
    }

    /**
     * 지금 포커스가 억류 상태인지 확인한다.
     *
     * @returns 억류 중이면 true
     */
    isSuspended(): boolean {
        return this.#suspended !== null;
    }

    /**
     * 억류해둔 위치로 캐럿/포커스를 복구한다.
     */
    resume(): void {
        const suspended = this.#suspended;
        if (suspended === null) {
            return;
        }
        this.#suspended = null;

        if (suspended.element !== null) {
            suspended.element.$getElement().getEl().focus();
            suspended.element.restoreCaret(suspended.caret);
            this.setElement(suspended.element);
        } else if (suspended.block !== null) {
            this.setBlock(suspended.block);
        }
    }

    /**
     * 억류해둔 상태를 버리고, 실제 포커스 상태로 업데이트한다.
     */
    discard(): void {
        if (this.#suspended === null) {
            return;
        }

        this.#suspended = null;
        this.setBlock(null);
    }

    /**
     * 마우스다운 지점을 기억해둔다.
     * resolveContainer() 호출 시 한 번 소모되고 버려진다.
     *
     * @param x - clientX
     * @param y - clientY
     */
    rememberPoint(x: number, y: number): void {
        this.#point = { x, y };
    }

    /**
     * 포커스된 블록을 가져온다.
     *
     * @returns 현재 포커스된 블록 (없으면 루트블록)
     */
    getBlock(): Block {
        return this.block ?? this.editor.root;
    }

    /**
     * 포커스된 엘리먼트를 가져온다.
     *
     * @returns 포커스된 엘리먼트
     */
    getElement(): Element | null {
        return this.element;
    }

    /**
     * 현재 포커스된 블록을 설정한다.
     *
     * @param block - 설정할 블록 (null 이면 해제)
     */
    setBlock(block: Block | null): void {
        const changed = block !== this.block;

        if (this.element?.block !== block) {
            this.element?.onBlur();
            this.element?.$getElement().removeAttr('data-focused');
            this.element = null;
        }

        if (changed === true) {
            const previousBlock = this.block;
            this.block?.$getBlock().removeAttr('data-focused');
            this.block = block;
            block?.$getBlock().setAttr('data-focused', 'true');
            previousBlock?.onBlur();
        }

        block?.onFocus(this.element);

        if (changed === true) {
            this.editor.refreshBlocks();
            this.editor.refreshTools();
        }
    }

    /**
     * 현재 포커스된 엘리먼트를 설정한다.
     *
     * @param element - 설정할 엘리먼트
     */
    setElement(element: Element): void {
        if (element === this.element) {
            return;
        }

        this.element?.onBlur();
        this.element?.$getElement().removeAttr('data-focused');
        this.element = element;
        element.$getElement().setAttr('data-focused', 'true');
        element.onFocus();

        this.setBlock(element.block);
    }

    /**
     * 현재 DOM/선택 상태로부터 포커스된 엘리먼트를 계산한다.
     *
     * @param target - 기준 대상(마우스 클릭 등으로 명시적 지점이 있을 때)
     * @returns 계산된 엘리먼트
     */
    resolveElement(target: EventTarget | null = null): Element | null {
        let node: Node | EventTarget | null = target ?? this.editor.selection.getRange()?.startContainer ?? null;

        if (node instanceof Text) {
            node = node.parentElement;
        }

        const $start = Html.el(node as EventTarget);
        if ($start === null) {
            return null;
        }

        const $boundary = $start.getParents('rootin-block,rootin-element');
        if ($boundary === null || $boundary.is('rootin-element') === false) {
            return null;
        }

        const element = $boundary.getData('element') as Element;
        return element.kind === 'container' ? null : element;
    }

    /**
     * 현재 DOM/선택 상태로부터 포커스된 엘리먼트를 찾지 못하였다면, 포커스된 블록을 계산한다.
     *
     * @param target - 기준 대상(마우스 클릭 등으로 명시적 지점이 있을 때)
     * @returns 계산된 블록
     */
    resolveBlock(target: EventTarget | null = null): Block | null {
        let node: Node | EventTarget | null = target;
        if (node instanceof Text) {
            node = node.parentElement;
        }

        const $start = Html.el(node as EventTarget);
        if ($start === null) {
            return null;
        }

        const $boundary = $start.getParents('rootin-block,rootin-element');
        if ($boundary === null || $boundary.is('rootin-element') === true) {
            return null;
        }

        return $boundary.getData('block') as Block;
    }

    /**
     * 클릭 지점이 컨테이너 엘리먼트의 빈여백인 경우, 가까운 순서대로 포커스가능한 블록후보를 찾는다.
     *
     * @param target - 클릭 대상 (마우스다운 지점 기억이 없을 때의 대체 기준)
     * @returns 가까운 순서의 블록후보 목록 (컨테이너가 아니면 빈 배열)
     */
    resolveContainer(target: EventTarget | null = null): Block[] {
        const point = this.#point;
        this.#point = null;

        const start = point !== null ? document.elementFromPoint(point.x, point.y) : target;

        const $start = Html.el(start as EventTarget);
        if ($start === null) {
            return [];
        }

        const $boundary = $start.getParents('rootin-block,rootin-element');
        if ($boundary === null || $boundary.is('rootin-element') === false) {
            return [];
        }

        const element = $boundary.getData('element') as Element;
        if (element.kind !== 'container') {
            return [];
        }

        const $children = Html.all('> rootin-block', $boundary).getList();
        if (point === null || $children.length === 0) {
            return [element.block];
        }

        return $children
            .map(($child) => {
                const rect = $child.getEl().getBoundingClientRect();
                const dx = Math.max(rect.left - point.x, 0, point.x - rect.right);
                const dy = Math.max(rect.top - point.y, 0, point.y - rect.bottom);
                return { block: $child.getData('block') as Block, distance: Math.sqrt(dx * dx + dy * dy) };
            })
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 4)
            .map((candidate) => candidate.block);
    }
}
